/* eslint-disable max-lines */
import { StatParser } from './parser/parser'
import { collator, assocPath, createErrorType, getIn, is, pick } from './helpers'
// import debug from 'debug'
// const logger = debug('aytm:stats:schema-rules') // eslint-disable-line no-unused-vars

const ParseError = createErrorType('ParseError')
const ParserTypeError = createErrorType('ParserTypeError')
const ParserTimeoutError = createErrorType('ParseTimeoutError')
const ConvertToBasicError = createErrorType('ConvertToBasicError')

const defaultProp = Symbol('default')

const HEATMAP_CLUSTER = 'hm.cluster'
const RANGE_ID = '\\d+[-:]\\d+|(?:\\d+\\.)+\\d+|(?:\\d+,)+\\d+'
const rangeRegExp = new RegExp(RANGE_ID)

const removeQuotes = text => {
  const match =
    'string' == typeof text
      ? text.match(/^"([^"]*)"|'([^']*)'$/) // eslint-disable-line no-useless-escape
      : false
  return match && (match[1] || match[2]) ? match[1] || match[2] : text
}

// CHECK for tags
const saveArray = (left, right, savePlain = false) => {
  const res = { type: 'ArrayExpression', elements: [] }
  ;[left, right]
    .filter(v => null != v)
    .forEach(el => {
      if (is(String)(el) || is(Number)(el)) {
        res.elements.push({ type: 'Literal', value: el, raw: `${el}` })
      } else if (el.type && el.elements) {
        res.elements = res.elements.concat(el.elements)
      } else if (['Literal', 'Identifier'].includes(el.type)) {
        res.elements.push(el)
      } else if (Array.isArray(el)) {
        res.elements = res.elements.concat(el)
      }
    })
  return savePlain ? res.elements : res
}

const memberExpressionFromString = str => {
  if (null == str) {
    return { type: 'Literal', value: null, raw: 'null' }
  }
  const result = { type: 'Identifier' }
  const i = str.lastIndexOf('.')
  if (/^(.*)\[(\d+)]$/.test(str)) {
    Object.assign(result, {
      type: 'MemberExpression',
      computed: true,
      object: memberExpressionFromString(RegExp.$1),
      property: { type: 'Literal', value: parseInt(RegExp.$2, 10), raw: RegExp.$2 }
    })
  } else if (0 < i) {
    const obj = str.slice(0, i)

    const prop = str.slice(i + 1)
    Object.assign(result, {
      type: 'MemberExpression',
      computed: false,
      object: memberExpressionFromString(obj),
      property: memberExpressionFromString(prop)
    })
  } else Object.assign(result, { name: str })
  return result
}

const parseMemberExpression = obj =>
  null == obj
    ? null
    : is(String)(obj)
    ? memberExpressionFromString(obj)
    : Object.assign(
        {},
        obj,
        obj.left ? { left: parseMemberExpression(obj.left) } : {},
        obj.right ? { right: parseMemberExpression(obj.right) } : {}
      )

const MAX_RANGE_EXPANSION_LENGTH = 300
const logicalExpressionNode = (operator, elements) => {
  const node = { type: 'LogicalExpression', operator }
  return 1 < elements.length
    ? elements.reduce(
        (res, el) =>
          null == res.left
            ? Object.assign({}, node, { left: el })
            : null == res.right
            ? Object.assign(res, { right: el })
            : Object.assign({}, node, { left: res, right: el }),
        {}
      )
    : elements[0]
}

const parseRangePattern = ({ alias, type, subtype, value, location, initialAlias }, callback, yy, level = 0) => {
  // check for ranges: `-` and `:`
  const div = Math.min(...[alias.indexOf('-'), alias.indexOf(':')].filter(v => -1 < v))
  if (0 < div && Infinity > div) {
    const startSym = /^\d+$/.test(alias[div - 1])
      ? /^\d+$/.test(alias[div - 2])
        ? /^\d+$/.test(alias[div - 3])
          ? div - 3
          : div - 2
        : div - 1
      : 0
    const leftPart = alias.slice(0, startSym)
    let rightPart = alias.slice(div + 1)
    const endSym = /[^\d:-]/g.test(rightPart) ? rightPart.indexOf(rightPart.match(/[^\d:-]/g)[0]) : 10000
    rightPart = rightPart.slice(endSym)
    const middle = alias.slice(startSym, rightPart ? alias.indexOf(rightPart) : 10000)

    let limits = middle.split(alias[div])
    limits = limits.filter(v => !isNaN(parseInt(v, 10)) && v != '')

    if (!startSym || limits[1] - limits[0] > MAX_RANGE_EXPANSION_LENGTH) {
      yy.result.errors.push({ text: initialAlias, type: 'parse' })
      return false
    }
    const res = []
    for (let i = Math.min(limits[0], limits[1]); i <= Math.max(limits[0], limits[1]); i++) {
      res.push(
        rangeRegExp.test(leftPart + i + rightPart)
          ? parseRangePattern(
              { alias: leftPart + i + rightPart, type, subtype, value, location, initialAlias },
              callback,
              yy,
              level + 1
            )
          : callback({ alias: leftPart + i + rightPart, type, subtype, value, location, initialAlias })
      )
    }
    return ':' == alias[div] ? logicalExpressionNode('&&', res) : logicalExpressionNode('||', res)
  }
  // check for sequences: `.` and `,`
  const comma = Math.min(...[alias.indexOf('.'), alias.indexOf(',')].filter(v => -1 < v))
  if (0 < comma && Infinity > comma) {
    const startSym = /^\d+$/.test(alias[comma - 1])
      ? /^\d+$/.test(alias[comma - 2])
        ? /^\d+$/.test(alias[comma - 3])
          ? comma - 3
          : comma - 2
        : comma - 1
      : 0
    const leftPart = alias.slice(0, startSym)
    let rightPart = alias.slice(startSym + 1)
    const endSym = /[^\d,.]/g.test(rightPart) ? rightPart.indexOf(rightPart.match(/[^\d,.]/g)[0]) : 10000
    rightPart = rightPart.slice(endSym)
    const middle = alias.slice(startSym, rightPart ? alias.indexOf(rightPart) : 10000)

    const res = middle
      .split(alias[comma])
      .map(v =>
        rangeRegExp.test(leftPart + v + rightPart)
          ? parseRangePattern(
              { alias: leftPart + v + rightPart, type, subtype, value, location, initialAlias },
              callback,
              yy,
              level + 1
            )
          : callback({ alias: leftPart + v + rightPart, type, subtype, value, location, initialAlias })
      )
    // logger('sub-parse:', res.join(',' == alias[comma] ? ' AND ' : ' OR '))
    return ',' == alias[comma] ? logicalExpressionNode('&&', res) : logicalExpressionNode('||', res)
  }
  return alias
}

const isoDateRegexps = [
  '((\\d{4})-([01]\\d)-([0-3]\\d)(?:T|\\s+)([0-2]\\d):([0-5]\\d):([0-5]\\d\\.\\d+)([+-][0-2]\\d:?[0-5]\\d|Z)?)',
  '((\\d{4})-([01]\\d)-([0-3]\\d)(?:T|\\s+)([0-2]\\d):([0-5]\\d):([0-5]\\d)([+-][0-2]\\d:?[0-5]\\d|Z)?)',
  '((\\d{4})-([01]\\d)-([0-3]\\d)(?:T|\\s+)([0-2]\\d):([0-5]\\d)([+-][0-2]\\d:?[0-5]\\d|Z)?)'
].map(rgxp => new RegExp(rgxp))

const normalizeISODate = str => {
  for (const regex of isoDateRegexps) {
    const m = str.match(regex)
    if (m) {
      // eslint-disable-next-line no-unused-expressions
      !/\d\.\d+$/.test(m[7]) && (/\d+$/.test(m[7]) || (m[7] = '00'), (m[7] += '.000'))
      ;/([+-][0-2]\d:?[0-5]\d|Z)$/.test(m[8]) || (m[8] = 'Z')
      const dateString = `${m[2]}-${m[3]}-${m[4]}T${m[5]}:${m[6]}:${m[7]}${m[8]}`
      return new Date(dateString).toISOString()
    }
  }
  return str
}

const jisonParseError = (err, text) => {
  if (!(is(ParseError)(err) && err.data)) {
    return { error: err }
  }
  const errorMessage = err.message.split('\n')
  const expected = (getIn(['data', 'expected'], err) || []).map(s => s.replace(/'/g, ''))
  const token = getIn(['data', 'token'], err)
  let position = getIn(['data', 'loc', 'last_column'], err) || 0
  const recoveryPosition = getIn(['data', 'loc', 'first_column'], err) || position
  if ('Parse error on line' == errorMessage[0].slice(0, 19)) {
    errorMessage[3] = errorMessage[3]
      .replace(/^Expecting .+, got '(.+)'$/, 'Error: unexpected $1')
      .replace(/EOF$/, 'end of input')
  }
  if ('Lexical error on line' == errorMessage[0].slice(0, 21)) {
    const errorSubstringEllipsis = /^\.{3}/.test(errorMessage[1])
    position =
      text.indexOf(errorSubstringEllipsis ? errorMessage[1].slice(3) : errorMessage[1]) +
      errorMessage[2].length -
      1 -
      (errorSubstringEllipsis ? 3 : 0)
    errorMessage[3] = errorMessage[0].slice(22, -1).replace(/^\d+\.\s+/, 'Error: ')
  }
  const error = (1 < errorMessage.length ? errorMessage.slice(1, 4) : errorMessage).join('\n')
  return Object.assign(
    { error, position, recoveryPosition },
    expected && expected.length ? { expected: expected.filter(token => !['EOF', 'ERROR'].includes(token)) } : void 0,
    token && token.length ? { token } : void 0
  )
}

// const dummyFunction = () => void 0

const BOOLEAN_OPERATORS = ['AND', 'OR', 'NOT']
const NUMERIC_OPERATORS = ['ADD', 'SUB', 'MLT', 'DIV', 'LE', 'GE', 'LT', 'GT']

const saveRef = ({ result, surveySchema, options }) => {
  const { clusters = {}, geoTraitsRefs = [], allowUndefs = false, saveRefs = true } = options
  const refsList = surveySchema.refsList
  const addRefPosition = saveRefs
    ? (alias, type, subtype, [position]) => {
        // logger(`addRefPosition(${alias}, ${type}, ${subtype}, ${position})`)
        if (!Object.hasOwn(result.refs, alias)) {
          result.refs[alias] = { type, subtype, positions: [position] }
        } else if (-1 == result.refs[alias].positions.indexOf(position)) {
          result.refs[alias].positions.push(position)
        }
      }
    : () => {}
  const addCodebookPosition = saveRefs
    ? (alias, value, type, subtype, [position]) => {
        // console.log(`addTagPosition(${value}, ${type}, ${subtype}, ${position})`, scope)
        if (!Object.hasOwn(result.codebooks, value)) {
          result.codebooks[value] = {
            type,
            subtype: subtype,
            positions: [position],
            alias: []
          }
        } else if (-1 == result.codebooks[value].positions.indexOf(position)) {
          result.codebooks[value].positions.push(position)
        }
        if (Array.isArray(alias)) {
          alias.forEach(
            v =>
              'Identifier' == v.type &&
              -1 == result.codebooks[value].alias.indexOf(v.name) &&
              result.codebooks[value].alias.push(v.name)
          )
        } else {
          alias != undefined &&
            -1 == result.codebooks[value].alias.indexOf(alias) &&
            result.codebooks[value].alias.push(alias)
        }
      }
    : () => {}
  const addTagPosition = saveRefs
    ? (alias, value, type, subtype, [position], scope) => {
        // console.log(`addTagPosition(${value}, ${type}, ${subtype}, ${position})`, scope)
        if ('MemberExpression' === scope.type && scope.property && ['JUNK'].includes(scope.property.name)) {
          return
        }
        if (!Object.hasOwn(result.tags, value)) {
          result.tags[value] = {
            type,
            subtype: scope.property && ['SENTIMENT'].includes(scope.property.name) ? 'sentiment' : subtype,
            positions: [position],
            alias: []
          }
        } else if (-1 == result.tags[value].positions.indexOf(position)) {
          result.tags[value].positions.push(position)
        }
        if (Array.isArray(alias)) {
          alias.forEach(
            v =>
              'Identifier' == v.type &&
              -1 == result.tags[value].alias.indexOf(v.name) &&
              result.tags[value].alias.push(v.name)
          )
        } else {
          alias != undefined && -1 == result.tags[value].alias.indexOf(alias) && result.tags[value].alias.push(alias)
        }
      }
    : () => {}

  return ({ alias, type, subtype, value, location, scope, initialAlias } = {}) => {
    switch (type) {
      case 'ref':
        if (['tref', 'bref', 'nref', 'aref'].includes(subtype)) {
          if (refsList && refsList[alias]) {
            addRefPosition(alias, type, subtype, location.range)
            return alias
          } else if (allowUndefs && 'tref' === subtype) {
            addRefPosition(alias, type, subtype, location.range)
            return alias
          } else {
            result.errors.push(
              Object.assign(
                {
                  text: initialAlias,
                  type: 'ref',
                  subtype
                },
                location ? { position: location.range[0] } : {},
                alias != initialAlias ? { subText: alias } : {}
              )
            )
            return allowUndefs ? alias : void 0
          }
        }
        if ('codebook' === subtype) {
          // addRefPosition(alias, type, subtype, location.range)
          return alias
        }
        if ('tag' === subtype) {
          // addRefPosition(alias, type, subtype, location.range)
          return alias
        }
        return void 0

      case 'trait':
      case 'ntrait':
      case 'atrait': {
        const lcAlias = alias.toLowerCase()
        const trait = surveySchema.traits.find(t => lcAlias == t.id)

        if (trait && null !== trait.values) {
          !result.traits.includes(lcAlias) && result.traits.push(lcAlias)
          return alias === initialAlias ? geoTraitsRefs[defaultProp](alias) : lcAlias
        } else if (geoTraitsRefs[alias]) {
          !result.traits.includes(lcAlias) && result.traits.push(lcAlias)
          return geoTraitsRefs[alias]
        } else {
          result.errors.push({ text: alias, type: 'trait' })
        }
        return void 0
      }

      case 'asset': {
        switch (subtype) {
          case HEATMAP_CLUSTER: {
            const scopeQid = scope.name.match(/^q\d+/i)[0].toLowerCase()
            const clusterChecker = clusterNode => {
              const questionClusters = clusters[scopeQid] || []
              const clusterExists = questionClusters.some(
                cluster => 0 === collator.compare(cluster.title, clusterNode.value)
              )
              if (!clusterExists) {
                result.errors.push(
                  Object.assign(
                    {
                      text: clusterNode.value,
                      type: 'cluster',
                      scope: scopeQid
                    },
                    location ? { position: location.range[0] } : {}
                  )
                )
              }
            }
            if ('ArrayExpression' == alias.type) {
              alias.elements.forEach(clusterChecker)
            } else {
              clusterChecker(alias)
            }
            return alias
          }
          default:
            return alias
        }
      }

      case 'codebook': {
        // logger(`saveRef(${value.value}, ${type}, ${subtype},`, scope, `)`)
        if ('list' === subtype) {
          value.elements.forEach(codebook =>
            addCodebookPosition(alias, codebook.value, type, undefined, location.range, scope)
          )
        } else addCodebookPosition(alias, value.value, type, subtype, location.range, scope)
        return value
      }

      case 'tag': {
        // logger(`saveRef(${value.value}, ${type}, ${subtype},`, scope, `)`)
        if ('list' === subtype) {
          value.elements.forEach(tag => addTagPosition(alias, tag.value, type, undefined, location.range, scope))
        } else addTagPosition(alias, value.value, type, subtype, location.range, scope)
        return value
      }

      case 'error':
        result.errors.push({
          text: initialAlias,
          type: 'ref',
          subText: alias != initialAlias ? alias : ''
        })
        return void 0

      default:
        return false
    }
  }
}

const saveTokensFromParser = ({ result, parser }) => {
  if (result.tokens.length) return void 0

  const tokens = parser.tree.yy.lexDebugger.map(token => {
    const parenthesesDepth = token.lexerStack.filter(st => 'PARENTHESES' === st).length
    return Object.assign(
      {
        position: token.tokenPosition,
        token: token.tokenName[0],
        match: token.tokenText
      },
      null != token.expected
        ? {
            expected: token.expected
              .filter(t => 'ERROR' !== t)
              .filter(t => 'EOF' !== t || !parenthesesDepth)
              .filter(t => 'RPAREN' !== t || parenthesesDepth)
          }
        : {}
    )
  })
  if (1 < tokens.length && 'EOF' === tokens[tokens.length - 1].token) {
    tokens.splice(-1, 1)
  }
  Object.assign(result, {
    tokens
  })
  return void 0
}

export default class SchemaRules {
  constructor(options = {}) {
    this.options = options
    this.parser = this._initParser()
  }

  _getSchemaTokenTypes() {
    const { isAdmin, getSurveySchema } = this.options
    const surveySchema = getSurveySchema()
    const SCHEMA_TYPES = {}
    for (const trait of surveySchema.traits) {
      if (isAdmin || 'panel' !== trait.id) {
        if ('n' === trait.type) {
          SCHEMA_TYPES[trait.id.toUpperCase()] = 'NTRAIT'
        } else if ('a' === trait.type) {
          SCHEMA_TYPES[trait.id.toUpperCase()] = 'ATRAIT'
        } else {
          SCHEMA_TYPES[trait.id.toUpperCase()] = 'TRAIT'
        }
      }
    }
    for (const trait of surveySchema.geoTraits) {
      SCHEMA_TYPES[trait.toUpperCase()] = 'TRAIT'
    }
    const { variables = {}, pipingVariables = {}, customVariables = {} } = surveySchema || {}
    const allVariables = Object.entries(variables).concat(
      Object.entries(pipingVariables),
      Object.entries(customVariables)
    )
    for (const [name, variable] of allVariables) {
      if ('n' === variable.type) {
        SCHEMA_TYPES[name.toUpperCase()] = 'NTOKEN'
      } else if ('a' === variable.type) {
        SCHEMA_TYPES[name.toUpperCase()] = 'ATOKEN'
      } else {
        SCHEMA_TYPES[name.toUpperCase()] = 'TOKEN'
      }
    }
    return SCHEMA_TYPES
  }

  _getParser() {
    const { getSurveySchema, refsList: thisRefsList } = this.options
    const surveySchema = getSurveySchema()
    const refsList = thisRefsList || surveySchema.refsList
    const types = this._getSchemaTokenTypes()

    const parser = new StatParser()
    parser.yy.TYPES_INFO = types
    parser.yy.REFS_INFO = refsList
    return parser
  }

  _initParser() {
    const { getSurveySchema } = this.options

    const parser = {
      tree: this._getParser(),
      getSurveySchema
    }

    parser.tree.yy.normalizeISODate = normalizeISODate
    parser.tree.yy.removeQuotes = removeQuotes
    parser.tree.yy.saveArray = saveArray
    parser.tree.lexer.options = { ranges: true }
    parser.tree.yy.parseError = (str, hash) => {
      throw new ParseError(str, hash)
    }

    return parser
  }

  // analyzeString({ filter = 'TRUE', allowUndefs = false, saveTokens = true, fixErrors = false } = {}) {
  analyzeString({
    filter = 'TRUE',
    allowUndefs = false,
    fixErrors = false,
    saveTokens = fixErrors,
    numberAllowed = false,
    clusters = {}
  } = {}) {
    const parser = this.parser
    const { getSurveySchema } = this.options
    const surveySchema = getSurveySchema()

    const geoTraitsRefs = surveySchema.geoTraits.reduce(
      (res, t) => ((res[t.toUpperCase()] = `${t.toUpperCase()}`), res),
      {
        [defaultProp]: name => `${name.toUpperCase()}`
      }
    )

    const result = {
      refs: {},
      codebooks: {},
      tags: {},
      traits: [],
      errors: [],
      tokens: []
    }

    parser.tree.yy.HEATMAP_CLUSTER = HEATMAP_CLUSTER

    let expression = {}
    let expressionText = filter.toUpperCase().trim()

    if (numberAllowed && expressionText.length && Number.isInteger(Number(expressionText))) {
      const errors = []
      if (Number(expressionText) <= 0) {
        errors.push({ text: Number(expressionText), type: 'number' })
      }
      return {
        expression: {
          status: {
            refs: {},
            codebooks: {},
            tags: {},
            errors
          },
          text: expressionText,
          tree: expression
        }
      }
    }

    if (saveTokens) {
      parser.tree.yy.lexDebugger = []
      parser.tree.yy.lexDebuggerReduceTokens = ['LPAREN', 'RPAREN', 'LT', 'LE', 'GT', 'GE', 'AND', 'OR']
    }
    parser.tree.yy.result = result

    const parseRef = ({ alias, type, subtype, value, location, callback, initialAlias, scope } = {}) => {
      const res = rangeRegExp.test(alias)
        ? parseRangePattern({ alias, type, subtype, value, location, initialAlias, scope }, callback, parser.tree.yy)
        : result.errors.some(error => error.text === alias)
        ? alias
        : callback({ alias, type, subtype, value, location, initialAlias, scope })
      // logger(`parseRef(${type}, ${subtype}, ${alias}, ${initialAlias})` /* , ${subAlias})` */, ': res =', res)
      return alias !== initialAlias
        ? res
        : is(Function)(res)
        ? { type: 'BoundFunction', foo: res }
        : parseMemberExpression(res)
    }

    // parser.tree.yy.logger = logger
    parser.tree.yy.saveRef = ({ alias, type = 'ref', subtype, value, scope, location = {} } = {}) =>
      parseRef({
        alias,
        type,
        subtype,
        location,
        value,
        callback: saveRef({ result, surveySchema, options: { clusters, geoTraitsRefs, allowUndefs } }),
        initialAlias: alias,
        scope
      })

    let retryCount = 0
    const retryLimit = fixErrors ? 5 : 1
    if (0 < expressionText.length || saveTokens) {
      let tryFixExpression = true
      while (tryFixExpression && retryLimit > retryCount) {
        retryCount++
        tryFixExpression = false
        try {
          if (0 < expressionText.length || saveTokens) {
            expression = parser.tree.parse(expressionText)
          } else {
            result.errors.push({ text: filter.toUpperCase(), type: 'parse' })
          }
        } catch (error) {
          const jisonError = jisonParseError(error, expressionText)
          if (2 > retryCount) {
            result.errors.push(Object.assign({ text: filter.toUpperCase(), type: 'parse' }, jisonError))
          }
          if (saveTokens && null != jisonError.token && null != jisonError.expected) {
            saveTokensFromParser({ result, parser })
            'EOF' === jisonError.token &&
              (result.tokens[result.tokens.length - 1].expected = jisonError.expected.slice())
          }
          if (retryLimit <= retryCount) continue

          if (null == jisonError.token && null == jisonError.expected) {
            expressionText =
              expressionText.slice(0, jisonError.position) +
              expressionText.slice(jisonError.position).replace(/^\s*\S+/, '')
            tryFixExpression = true
            // logger('retrying parse with fixed expression 1:', expressionText)
          } else if ('EOF' === jisonError.token && 'RPAREN' === jisonError.expected[0]) {
            expressionText = `${expressionText.slice(0, jisonError.position)})${expressionText.slice(
              jisonError.position
            )}`
            tryFixExpression = true
            // logger('retrying parse with fixed expression 2:', expressionText)
          } else if (
            'EOF' === jisonError.token &&
            ![...NUMERIC_OPERATORS, ...BOOLEAN_OPERATORS].some(k => jisonError.expected.includes(k))
          ) {
            expressionText = expressionText.replace(/\s+\S+$/, '')
            tryFixExpression = true
            // logger('retrying parse with fixed expression 3:', expressionText)
          } else if ('TOKEN' === jisonError.token) {
            const recoverPos = jisonError.recoveryPosition || jisonError.position
            expressionText =
              expressionText.slice(0, recoverPos) +
              expressionText.slice(recoverPos).replace(/^\s*@?[A-Z][\dA-Z_]*\b/, '')
            tryFixExpression = true
            // logger('retrying parse with fixed expression 4:', expressionText)
          }
        }
      }
    }
    // fix tokens 'expected' field, remove last EOF token
    if (saveTokens) saveTokensFromParser({ result, parser })

    const status = pick(['refs', 'codebooks', 'tags', 'traits', 'tokens', 'errors'], result)
    status.tokens &&
      (status.tokens = status.tokens.map(token => pick(['token', 'match', 'position', 'expected'], token)))
    return {
      expression: {
        status,
        text: expressionText,
        tree: expression
      }
    }
  }
}

SchemaRules.ParseError = ParseError
SchemaRules.ParserTypeError = ParserTypeError
SchemaRules.ConvertToBasicError = ConvertToBasicError

// const unassociativeOp = operator => ['-', '/', '||'].includes(operator)
const unassociativeOp = operator => ['-', '/'].includes(operator)
const operatorPrecedenceList = [
  ['&&', '||'],
  ['==', '!=', '<', '<=', '>', '>=', 'in', 'not_in', 'like'],
  ['codebook_eq', 'codebook_neq', 'codebook_in'],
  ['tag_eq', 'tag_neq', 'tag_in'],
  ['min', 'max', 'minposition', 'maxposition'],
  ['count'],
  ['-', '+'],
  ['/', '*']
]
const operatorStringMap = {
  '&&': 'and',
  '||': 'or',
  not_in: 'not in',
  codebook_eq: '=',
  codebook_neq: '!=',
  codebook_in: 'in',
  tag_eq: '=',
  tag_neq: '!=',
  tag_in: 'in',
  '==': '=',
  '!=': '!=',
  '!': 'not',
  in_section: 'IN SECTION'
}
const operatorToString = operator =>
  operatorStringMap[operator] ? operatorStringMap[operator] : operator.toUpperCase()

const operatorPrecedence = (a, b) =>
  operatorPrecedenceList.reduce((res, gr, i) => (-1 < res ? res : gr.includes(a) ? i : res), -1) -
  operatorPrecedenceList.reduce((res, gr, i) => (-1 < res ? res : gr.includes(b) ? i : res), -1)

const stringify = (node, stack = []) => {
  if ('BinaryExpression' === node.type || 'LogicalExpression' === node.type) {
    const [stackTop] = stack.slice(-1)
    const parenthesize =
      stackTop &&
      ((unassociativeOp(node.operator) && unassociativeOp(stackTop.operator) && stackTop.right == node) ||
        0 > operatorPrecedence(node.operator, stackTop.operator) ||
        (0 == operatorPrecedence(node.operator, stackTop.operator) && stackTop.right == node))
    const expression = [
      stringify(node.left, stack.concat(node)),
      operatorToString(node.operator),
      stringify(node.right, stack.concat(node))
    ].join(' ')
    return parenthesize ? ['(', expression, ')'].join('') : expression
  } else if ('UnaryExpression' === node.type) {
    if ('!' === node.operator && 'in' == node.argument.operator) {
      return stringify(Object.assign({}, node.argument, { operator: 'not_in' }), stack.concat(node))
    } else if (node.argument.operator) {
      return `${operatorToString(node.operator)} (${stringify(node.argument, stack.concat(node))})`
    }
    return `${operatorToString(node.operator)} ${stringify(node.argument, stack.concat(node))}`
  } else if ('ArrayExpression' === node.type) {
    return node.elements.map(el => stringify(el, stack.concat(node))).join(', ')
  } else if ('MemberExpression' === node.type) {
    return `${stringify(node.object, stack)}.${stringify(node.property, stack)}`
  } else if ('Identifier' === node.type) {
    return node.name
  } else if ('Literal' === node.type) {
    return null == node.value
      ? 'NULL'
      : is(String)(node.value)
      ? `"${node.value}"`
      : is(Date)(node.value)
      ? node.value.toISOString()
      : node.value
  }
  return node
}
SchemaRules.stringify = stringify

const allowedTime = 7
const checkAllowedTime = inception => {
  if (allowedTime < Date.now() / 1000 - inception) {
    throw new ParserTimeoutError(`Took too long: ${Date.now() / 1000 - inception}`)
  }
}

const findDeepOperator = (inception, node, operators = [], stack = []) => {
  checkAllowedTime(inception)
  for (const operator of operators) {
    if (
      operator === node.operator &&
      (!['in_shape', 'in_section'].includes(node.operator) ||
        (['in_shape', 'in_section'].includes(node.operator) && 'Identifier' === node.left.type && 'ArrayExpression' === node.right.type)) &&
      0 < stack.length &&
      stack.some(op => op !== operator)
    )
      return true
  }
  const newStack = node.operator ? stack.concat([node.operator]) : stack.slice()
  return (
    (node.left && findDeepOperator(inception, node.left, operators, newStack)) ||
    (node.right && findDeepOperator(inception, node.right, operators, newStack)) ||
    (node.argument && findDeepOperator(inception, node.argument, operators, newStack))
  )
}

const unaryExpressionNode = (operator, argument) => {
  return {
    type: 'UnaryExpression',
    operator: operator,
    prefix: true,
    argument: argument
  }
}

const operatorsInverse = {
  not_in: 'in',
  in: 'not_in',
  '>': '<=',
  '<': '>=',
  '>=': '<',
  '<=': '>',
  '!=': '==',
  '==': '!='
}

/*
from : Q1A1 AND (Q2A2 OR Q2A3) AND GENDER = "m"
to   : Q1A1 AND Q2A2 AND GENDER = "m" OR Q1A1 AND Q2A3 AND GENDER = "m"
*/
const _flattenInShape = node => {
  if ('BinaryExpression' === node.type && ['in_shape', 'in_section'].includes(node.operator)) {
    const { left, right, operator } = node

    if ('Identifier' === left.type && 'ArrayExpression' === right.type) {
      node.left = {
        left: { ...left },
        operator,
        right: {
          ...right.elements.shift()
        },
        type: 'BinaryExpression'
      }
      node.operator = '||'
      node.right = {
        left: { ...left },
        operator,
        right: {
          ...(1 < right.elements.length ? right : right.elements.shift())
        },
        type: 'BinaryExpression'
      }
      node.type = 'LogicalExpression'

      _flattenInShape(node.right)
    }
  }
}

const _flatten = (inception, node, tree = node, path = []) => {
  checkAllowedTime(inception)

  if ('LogicalExpression' === node.type) {
    _flattenInShape(node.left)
    _flattenInShape(node.right)

    if ('&&' === node.operator) {
      node.left = _flatten(inception, node.left, tree, path.concat('left'))
      node.right = _flatten(inception, node.right, tree, path.concat('right'))
    } else if ('||' === node.operator) {
      if (findDeepOperator(inception, node.left, ['||', 'in_shape', 'in_section'])) {
        node.left = _flatten(inception, node.left)
      } else if (findDeepOperator(inception, node.right, ['||', 'in_shape', 'in_section'])) {
        node.right = _flatten(inception, node.right)
      } else if (0 < path.length) {
        const parent = getIn(path.slice(0, -1), tree)
        const left = assocPath(path.slice(-1), node.left)(parent)
        const right = assocPath(path.slice(-1), node.right)(parent)
        Object.assign(parent, {
          type: 'LogicalExpression',
          operator: '||',
          left,
          right
        })
        return 'left' == path[path.length - 1] ? left : right
      }
    }
  }
  if ('BinaryExpression' === node.type) {
    _flattenInShape(node)

    if ('LogicalExpression' === node.type) {
      return _flatten(inception, node, tree, path)
    }

    node.left = _flatten(inception, node.left, tree, path.concat('left'))
    node.right = _flatten(inception, node.right, tree, path.concat('right'))
  }
  if ('UnaryExpression' === node.type) {
    if (node.type === node.argument.type && node.operator == node.argument.operator) {
      const cleanUp = !node.argument.argument
      // eslint-disable-next-line no-param-reassign
      node = node.argument.argument
      if (cleanUp) {
        delete node.argument
        delete node.prefix
      }
      // eslint-disable-next-line no-param-reassign
      if (findDeepOperator(inception, node.argument, [node.operator], [''])) node = _flatten(inception, node)
    }
    if ('LogicalExpression' === node.argument.type) {
      // eslint-disable-next-line no-param-reassign
      node = {
        type: node.argument.type,
        operator: '&&' == node.argument.operator ? '||' : '&&',
        left: unaryExpressionNode(node.operator, node.argument.left),
        right: unaryExpressionNode(node.operator, node.argument.right)
      }
      node.left = _flatten(inception, node.left, tree, path.concat('left'))
      node.right = _flatten(inception, node.right, tree, path.concat('right'))
    } else if ('BinaryExpression' === node.argument.type && operatorsInverse[node.argument.operator]) {
      // eslint-disable-next-line no-param-reassign
      node = { ...node.argument, operator: operatorsInverse[node.argument.operator] }
    } else if ('Literal' === node.argument.type && '-' === node.operator && is(Number)(node.argument.value)) {
      // eslint-disable-next-line no-param-reassign
      node = { type: 'Literal', value: -node.argument.value, text: `-${node.argument.value}` }
    }
  }

  return node == tree && 'LogicalExpression' === node.type && findDeepOperator(inception, node, ['||', 'in_shape', 'in_section'])
    ? _flatten(inception, node)
    : node
}

const flatten = (node, tree = node, path = []) => {
  const inception = Date.now() / 1000

  try {
    return _flatten(inception, node, tree, path)
  } catch {
    return null
  }
}
SchemaRules.flatten = flatten
